import React, { useEffect, useState } from 'react';
import { Card } from '../common/Card';
import { getAffiliateReferrals } from '../../services/firebase/affiliateProfile';
import { User } from 'lucide-react';

interface AffiliatePerformanceProps {
  referralCode: string;
  affiliateId: string;
}

export const AffiliatePerformance: React.FC<AffiliatePerformanceProps> = ({ referralCode: _referralCode, affiliateId }) => {
  const [referrals, setReferrals] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReferrals = async () => {
      if (!affiliateId) return;
      try {
        const data = await getAffiliateReferrals(affiliateId);
        setReferrals(data);
      } catch (error) {
        console.error('Failed to load referrals', error);
      } finally {
        setLoading(false);
      }
    };

    fetchReferrals();
  }, [affiliateId]);

  // Simple growth calculation (last 30 days)
  const last30Days = referrals.filter(r => {
    const date = new Date(r.createdAt);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return date >= thirtyDaysAgo;
  }).length;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Growth Summary */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-neutral-900 mb-4">Growth (Last 30 Days)</h3>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-3xl font-bold text-primary-600">{last30Days}</p>
              <p className="text-sm text-neutral-500">New signups</p>
            </div>
            <div className="h-16 w-32 bg-primary-50 rounded flex items-center justify-center text-xs text-primary-400">
              [Chart Placeholder]
            </div>
          </div>
        </Card>

        {/* Recent Activity Summary */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-neutral-900 mb-4">Activity Summary</h3>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-neutral-600">Total Referrals</span>
              <span className="font-medium">{referrals.length}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-neutral-600">Active Subscriptions</span>
              <span className="font-medium">
                {referrals.filter(r => r.subscriptionStatus === 'active').length}
              </span>
            </div>
          </div>
        </Card>
      </div>

      {/* Recent Referrals List */}
      <Card className="overflow-hidden">
        <div className="px-6 py-4 border-b border-neutral-200">
          <h3 className="text-lg font-semibold text-neutral-900">Recent Referrals</h3>
        </div>
        {loading ? (
          <div className="p-8 text-center text-neutral-500">Loading...</div>
        ) : referrals.length > 0 ? (
          <div className="divide-y divide-neutral-200">
            {referrals.slice(0, 10).map((referral) => (
              <div key={referral.id} className="px-6 py-4 flex items-center justify-between">
                <div className="flex items-center">
                  <div className="h-8 w-8 rounded-full bg-neutral-100 flex items-center justify-center text-neutral-500 mr-3">
                    <User className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-neutral-900">
                      User {referral.id.substring(0, 6)}...
                    </p>
                    <p className="text-xs text-neutral-500">
                      Joined {new Date(referral.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    referral.subscriptionStatus === 'active' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-neutral-100 text-neutral-800'
                  }`}>
                    {referral.subscriptionStatus || 'Free'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center text-neutral-500">
            No referrals yet. Share your link to get started!
          </div>
        )}
      </Card>
    </div>
  );
};
